import { compose } from 'recompose';
import { connect } from 'react-redux';
import { showToast } from 'modules/layout/layout.actions';

import React from 'react';

import {
  deleteAuthorizationBank,
  getAuthorizationBanking,
  postAuthorizationBank,
  updateAuthorizationBank
} from '../store/actions/authorizations.actions';
import {
  DELETE_AUTHORIZATIONS_FAILURE,
  GET_AUTHORIZATION_BANKING_FAILURE,
  POST_AUTHORIZATION_BANKING_FAILURE,
  UPDATE_AUTHORIZATION_BANKING_FAILURE
} from '../store/types/authorizationTypes';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import BankingCard from 'modules/common/bankingCard.component';

const sanitizeBankValues = (values, overrides = {}) => {
  const emptyGuid = '00000000-0000-0000-0000-000000000000';

  return {
    ...values,
    defaultAccount: !!values.defaultAccount,
    usedForWire: !!values.usedForWire,
    usedForAch: !!values.usedForAch,
    intermediaryRequired: !!values.intermediaryRequired,
    transfereeCanMakeChangesToThisAccount: !!values.transfereeCanMakeChangesToThisAccount,
    transfereeCanSubmitExpenseReportsToThisAccount: !!values.transfereeCanSubmitExpenseReportsToThisAccount,
    accountCurrencyId: values.accountCurrencyId || emptyGuid,
    accountTypeId: values.accountTypeId || emptyGuid,
    bankCountryId: values.bankCountryId || emptyGuid,
    bankProvinceId: values.bankProvinceId || emptyGuid,
    statusId: values.statusId || emptyGuid,
    accountLocationId: values.accountLocationId || emptyGuid,
    paymentTypeId: values.paymentTypeId || emptyGuid,
    isPaymentByDueDate: values.paymentSelector === 0,
    isPaymentByDiscountRate: values.paymentSelector === 1,
    ...overrides
  };
};

const AuthorizationBankingCard = props => {
  const { authorizationBanks, authorizationId, isReadOnly, addresses } = props;

  let authBanksMapped =
    (authorizationBanks &&
      Array.isArray(authorizationBanks) &&
      authorizationBanks.map(bank => {
        return {
          ...bank,
          paymentSelector: bank.isPaymentByDueDate ? 0 : bank.isPaymentByDiscountRate ? 1 : 0
        };
      })) ||
    [];

  const updateBank = async (record, values) => {
    const payload = sanitizeBankValues(values, {
      bankId: record.original.bankId,
      authorizationId
    });

    const resp = await props.updateAuthorizationBank(authorizationId, payload);

    if (resp.type === UPDATE_AUTHORIZATION_BANKING_FAILURE) {
      props.showToast('Error updating banking information.', TOAST_MESSAGE_SEVERITY_ERROR);
      return false;
    }

    if (resp.response && Array.isArray(resp.response)) {
      const hasError = resp.response.some(bank => bank.errorSyncingToAcumatica === true);
      if (hasError) {
        props.showToast('Bank updated but failed to sync to Acumatica. Please contact support.', TOAST_MESSAGE_SEVERITY_ERROR);
        return false;
      }
    } else if (resp.response?.errorSyncingToAcumatica === true) {
      props.showToast('Bank updated but failed to sync to Acumatica. Please contact support.', TOAST_MESSAGE_SEVERITY_ERROR);
      return false;
    }

    props.showToast('Bank updated successfully.', TOAST_MESSAGE_SEVERITY_SUCCESS);
    return true;
  };

  const createBank = async values => {
    const payload = sanitizeBankValues(values, {
      authorizationId,
      bankId: 0
    });

    const resp = await props.postAuthorizationBank(authorizationId, payload);
    if (resp.type === POST_AUTHORIZATION_BANKING_FAILURE) {
      props.showToast('Error creating banking information', TOAST_MESSAGE_SEVERITY_ERROR);
      return false;
    }
    if (resp.response?.errorSyncingToAcumatica === true) {
      props.showToast('Bank created but failed to sync to Acumatica. Please contact support.', TOAST_MESSAGE_SEVERITY_ERROR);
    }
    return true;
  };

  const deleteBank = async record => {
    const id = record.original.bankId;
    const resp = await props.deleteAuthorizationBank(authorizationId, id);
    if (resp.type === DELETE_AUTHORIZATIONS_FAILURE) {
      props.showToast('Error deleting banking information', TOAST_MESSAGE_SEVERITY_ERROR);
      return false;
    }
    return true;
  };

  const getBankingInformation = async () => {
    const resp = await props.getAuthorizationBanking(authorizationId);
    if (resp.type === GET_AUTHORIZATION_BANKING_FAILURE) {
      props.showToast('Error fetching banking information', TOAST_MESSAGE_SEVERITY_ERROR);
      return false;
    }
    return true;
  };

  return (
    <BankingCard
      banks={authBanksMapped}
      createBankFunc={createBank}
      updateBankFunc={updateBank}
      deleteBankFunc={deleteBank}
      getBankingInformation={getBankingInformation}
      isReadOnly={isReadOnly}
      isAuthorization={true}
      authorizationAddress={addresses}
      fileId={authorizationId}
    />
  );
};

export default compose(
  connect(null, {
    getAuthorizationBanking,
    postAuthorizationBank,
    updateAuthorizationBank,
    deleteAuthorizationBank,
    showToast
  })
)(AuthorizationBankingCard);
